import { OVERLAY_CONFIG } from '@shared/config'

/**
 * Низкоуровневый HTTP-клиент для сессии оверлея.
 * SRP: только сетевые вызовы, без оркестрации/состояния.
 *
 * Все запросы идут с credentials: 'include' — браузер сам отправляет и
 * принимает сессионную cookie (HttpOnly). Фронт НЕ читает и НЕ хранит токен
 * сессии в JS — только обменивает короткоживущий bootstrap-токен на cookie.
 */

export interface PublicKeyResponse {
  /** Публичный ключ Ed25519: base64url(raw 32 байта) или base64(SPKI) */
  publicKey: string
  /** Идентификатор/версия ключа — опционально, для ротации */
  keyId?: string
  alg?: 'Ed25519'
}

export class SessionApi {
  private readonly base: string

  constructor(baseUrl: string = OVERLAY_CONFIG.API_BASE_URL) {
    this.base = baseUrl.replace(/\/+$/, '')
  }

  /**
   * Обмен bootstrap-токена (из URL) на сессионную cookie.
   * Бэкенд при успехе ставит Set-Cookie: session_id=...; HttpOnly.
   * @returns true при HTTP 2xx
   */
  async exchangeToken(token: string): Promise<boolean> {
    const res = await fetch(`${this.base}/auth/overlay/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    return res.ok
  }

  /**
   * Продление сессии. Используется когда cookie истекла, но рефреш ещё валиден
   * (бэкенд решает по своей сессии/refresh-cookie). При успехе ставит новую
   * сессионную cookie.
   * @returns true при HTTP 2xx
   */
  async refresh(): Promise<boolean> {
    const res = await fetch(`${this.base}/auth/overlay/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    return res.ok
  }

  /**
   * Получение публичного ключа подписи плагинов. Требует валидной сессии
   * (cookie). 401 → сессия истекла.
   */
  async fetchPublicKey(): Promise<PublicKeyResponse | null> {
    const res = await fetch(`${this.base}/auth/overlay/public-key`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    try {
      return (await res.json()) as PublicKeyResponse
    } catch {
      return null
    }
  }
}

export const sessionApi = new SessionApi()
